import { useEffect, useState } from 'react';
import {
  getRegistrationRequests,
  type AdminVendorRegistrationRequest,
} from '../../api/admin';
import { ApiError } from '../../api/client';

const PAGE_SIZE = 20;

function formatDateTime(value: string | null | undefined) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  const hour = String(parsed.getHours()).padStart(2, '0');
  const minute = String(parsed.getMinutes()).padStart(2, '0');
  return `${parsed.getFullYear()}.${month}.${day} ${hour}:${minute}`;
}

export function AdminRegistrationRequestsPage() {
  const [activeOnly, setActiveOnly] = useState(true);
  const [page, setPage] = useState(0);
  const [items, setItems] = useState<AdminVendorRegistrationRequest[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    getRegistrationRequests(activeOnly, page, PAGE_SIZE)
      .then((list) => {
        if (cancelled) return;
        setItems(list.items);
        setTotalCount(list.totalCount);
      })
      .catch((err) => {
        if (cancelled) return;
        setItems([]);
        setError(
          err instanceof ApiError && err.message
            ? err.message
            : '등록 요청 목록을 불러오지 못했습니다.',
        );
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeOnly, page]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <>
      <h1 className="partner-title">업체 등록 요청</h1>
      <p className="partner-subtitle">
        사용자가 지도에서 남긴 미등록 업체 등록 요청입니다. 최근 요청순으로
        표시됩니다.
      </p>

      <div className="admin-tabs" role="tablist" aria-label="등록 요청 필터">
        <button
          aria-selected={activeOnly}
          className={`admin-tab${activeOnly ? ' admin-tab--active' : ''}`}
          onClick={() => {
            setActiveOnly(true);
            setPage(0);
          }}
          role="tab"
          type="button"
        >
          접수 중
        </button>
        <button
          aria-selected={!activeOnly}
          className={`admin-tab${!activeOnly ? ' admin-tab--active' : ''}`}
          onClick={() => {
            setActiveOnly(false);
            setPage(0);
          }}
          role="tab"
          type="button"
        >
          전체
        </button>
      </div>

      {error ? <div className="partner-error">{error}</div> : null}

      {loading ? (
        <p className="partner-empty">불러오는 중…</p>
      ) : items.length === 0 ? (
        <div className="partner-card">
          <p className="partner-empty">등록 요청이 없습니다.</p>
        </div>
      ) : (
        <div className="partner-card">
          <div className="partner-table-wrap">
            <table className="partner-table">
              <thead>
                <tr>
                  <th>업체명</th>
                  <th>주소</th>
                  <th>요청 수</th>
                  <th>출처</th>
                  <th>최초 요청</th>
                  <th>최근 요청</th>
                  <th>상태</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.requestKey}>
                    <td>{item.vendorName}</td>
                    <td>{item.roadAddress ?? '—'}</td>
                    <td>{item.requestCount}</td>
                    <td>{item.source}</td>
                    <td>{formatDateTime(item.firstRequestedAt)}</td>
                    <td>{formatDateTime(item.lastRequestedAt)}</td>
                    <td>
                      <span
                        className={
                          item.active
                            ? 'partner-badge partner-badge--yellow'
                            : 'partner-badge'
                        }
                      >
                        {item.active ? '접수 중' : '처리 완료'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 ? (
            <div className="admin-pagination">
              <button
                className="partner-btn partner-btn--ghost"
                disabled={page <= 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                type="button"
              >
                이전
              </button>
              <span className="partner-subtitle">
                {page + 1} / {totalPages}
              </span>
              <button
                className="partner-btn partner-btn--ghost"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                type="button"
              >
                다음
              </button>
            </div>
          ) : null}
        </div>
      )}
    </>
  );
}
